import { useFeatureFlag } from '@revenuewell/front-end-bundle';
import { ReactElement, useEffect, useState } from 'react';
import { useInsuranceApi } from '../../../../../../hooks/use-insurance-api';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { PatientVerificationSummary } from '../../../../../../services/insurance/types/patient-verification-summary';

type InsuranceNameProps = {
  type: 'primary' | 'secondary';
};

export const InsuranceName = ({ type }: InsuranceNameProps): ReactElement | string => {
  const { patientInfo, contactDetails, locationId } = usePatientProfile();
  const [isIv2Active, setIsIv2Active] = useState<boolean>(false);
  const [patientIv, setPatientIv] = useState<PatientVerificationSummary>();
  const { hasFlag } = useFeatureFlag();
  const { getPracticeOnboarding, getPatientVerificationSummaries } = useInsuranceApi();

  useEffect(() => {
    (async () => {
      if (hasFlag('InsuranceVerification2')) {
        const onboarding = await getPracticeOnboarding(locationId);
        const isActive = onboarding?.status === 'Active' && (onboarding?.isInsuranceVerificationDisplayActive || false);
        if (isActive) loadInsuranceVerifications();
        setIsIv2Active(isActive);
      }
    })();
  }, [locationId]);

  const loadInsuranceVerifications = async () => {
    const patientVerifications = await getPatientVerificationSummaries([contactDetails.id], locationId);
    if (patientVerifications.length > 0) setPatientIv(patientVerifications[0]);
  };

  if (type === 'primary') {
    // Show carrier name from IV2 if active and data is available
    if (isIv2Active && patientIv && patientInfo?.insuranceInfo?.carrierId) {
      return patientInfo.insuranceInfo.carrierId;
    }

    // Fallback: Show basic primary insurance info from API
    if (patientInfo?.insuranceInfo?.carrierId || patientInfo?.insurance) {
      return patientInfo.insuranceInfo?.carrierId || patientInfo.insurance || '-';
    }
  }

  if (type === 'secondary') {
    // Show carrier name from IV2 if active and data is available
    if (isIv2Active && patientIv && patientInfo?.secondaryInsuranceInfo?.carrierId) {
      return patientInfo.secondaryInsuranceInfo.carrierId;
    }

    // Fallback: Show basic secondary insurance info if available
    if (patientInfo?.secondaryInsuranceInfo?.carrierId) {
      return patientInfo.secondaryInsuranceInfo.carrierId;
    }
  }

  return '-';
};
